import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { isCrewdleStaff } from '../core/models/agent-maintenance.model';
import { FirebaseService } from '../core/services/firebase.service';
import { OrgContextService } from '../core/services/org-context.service';

/**
 * Data residency: the active org exists but its region can't be reached (no
 * config in this build, region not live, region-token refused). Say so
 * instead of bouncing to onboarding or reading another region's project.
 */
export function regionUnavailableRedirect(): UrlTree | null {
  const firebase = inject(FirebaseService);
  return firebase.regionError() ? inject(Router).parseUrl('/region-unavailable') : null;
}

export function requirePermission(permission: string, fallbackUrl = '/home'): CanActivateFn {
  return async () => {
    const orgContext = inject(OrgContextService);
    const router = inject(Router);
    const unavailable = regionUnavailableRedirect();
    if (unavailable) return unavailable;

    if (!orgContext.currentOrg()) {
      return router.parseUrl('/new');
    }

    const hasPermission = await orgContext.checkPermission(permission);
    return hasPermission || router.parseUrl(orgContext.orgUrl(fallbackUrl));
  };
}

export function requireOrg(): CanActivateFn {
  return async () => {
    const orgContext = inject(OrgContextService);
    const router = inject(Router);
    const unavailable = regionUnavailableRedirect();
    if (unavailable) return unavailable;

    const org = orgContext.currentOrg();
    return org ? true : router.parseUrl('/new');
  };
}

export function requireOrgAdmin(fallbackUrl = '/home'): CanActivateFn {
  return () => {
    const orgContext = inject(OrgContextService);
    const router = inject(Router);
    const unavailable = regionUnavailableRedirect();
    if (unavailable) return unavailable;

    if (!orgContext.currentOrg()) {
      return router.parseUrl('/new');
    }

    // flag: A non-admin cannot open the Folder Knowledge activation and retry controls.
    return orgContext.isCurrentUserAdmin() || router.parseUrl(orgContext.orgUrl(fallbackUrl));
  };
}

/** The signed-in user once Firebase Auth has settled (a guard can run before it has). */
function settledUser(firebase: FirebaseService): Promise<User | null> {
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(firebase.auth, (user) => {
      unsub();
      resolve(user && !user.isAnonymous ? user : null);
    });
  });
}

/**
 * Crewdle staff only: a verified @crewdle.com account (agent maintenance). The
 * callables behind such pages check the same rule server-side; this keeps the
 * page from opening for anyone else.
 */
export function requireCrewdleStaff(fallbackUrl = '/home'): CanActivateFn {
  return async () => {
    const firebase = inject(FirebaseService);
    const orgContext = inject(OrgContextService);
    const router = inject(Router);
    const user = await settledUser(firebase);
    return isCrewdleStaff(user) || router.parseUrl(orgContext.currentOrg() ? orgContext.orgUrl(fallbackUrl) : fallbackUrl);
  };
}
